import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TASK_STATUSES, TASK_STATUS_LABELS, TASK_PRIORITIES, statusBadgeClass, priorityBadgeClass } from "@/lib/constants";
import { Search, Calendar, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Pagination from "@/components/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { useAuth } from "@/contexts/AuthContext";

export default function TasksPage() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [myTasksOnly, setMyTasksOnly] = useState(false);

  const { items: tasks, total, page, pageSize, setPage, setPageSize } = usePaginatedList("/tasks", {
    q,
    status: statusFilter === "all" ? "" : statusFilter,
    priority: priorityFilter === "all" ? "" : priorityFilter,
    project_id: projectFilter === "all" ? "" : projectFilter,
    assignee_id: myTasksOnly ? me?.id : (assigneeFilter === "all" ? "" : assigneeFilter),
    from_date: fromDate,
    to_date: toDate,
  });

  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([api.get("/users/all"), api.get("/projects/all")]);
        setUsers(u.data);
        setProjects(p.data);
      } catch {}
    })();
  }, []);

  const userName = (id) => users.find((u) => u.id === id)?.name || "Unassigned";
  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  const resetFilters = () => {
    setQ("");
    setStatusFilter("all");
    setPriorityFilter("all");
    setProjectFilter("all");
    setAssigneeFilter("all");
    setFromDate("");
    setToDate("");
    setMyTasksOnly(false);
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Tasks</h1>
        <p className="text-sm text-muted-foreground mt-1">Global task list across every project.</p>
      </div>

      <div className="bg-card border border-border p-3.5 rounded-lg shadow-sm space-y-3">
        <div className="flex flex-wrap gap-2.5 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tasks by title..."
              className="pl-9 h-9 text-xs"
              data-testid="tasks-search"
            />
          </div>

          <label className="flex items-center gap-2 text-xs font-medium select-none cursor-pointer border rounded-md px-3 h-9 bg-background hover:bg-muted/30">
            <input
              type="checkbox"
              checked={myTasksOnly}
              onChange={(e) => setMyTasksOnly(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span>My Tasks</span>
          </label>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="tasks-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {TASK_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-36 h-9 text-xs" data-testid="tasks-filter-priority">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {TASK_PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="tasks-filter-project">
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!myTasksOnly && (
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="w-40 h-9 text-xs" data-testid="tasks-filter-assignee">
                <SelectValue placeholder="Assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All assignees</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {(q || statusFilter !== "all" || priorityFilter !== "all" || projectFilter !== "all" || assigneeFilter !== "all" || fromDate || toDate || myTasksOnly) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="h-9 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/40 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>Created Date:</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">From:</span>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">To:</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-36 h-8 text-xs font-mono"
            />
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-3 py-3 font-semibold text-center w-12">S.No</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Project</th>
                <th className="px-4 py-3 font-semibold">Assignee & Tagged</th>
                <th className="px-3 py-3 font-semibold">Priority</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold text-center">Est (h)</th>
                <th className="px-3 py-3 font-semibold text-center">Deadline Δ</th>
                <th className="px-3 py-3 font-semibold">Due</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t, i) => (
                <tr
                  key={t.id}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer"
                  data-testid={`task-row-${t.id}`}
                  onClick={() => t.project_id && navigate(`/projects/${t.project_id}?tab=tasks&taskId=${t.id}`)}
                >
                  <td className="px-3 py-3 font-mono text-xs text-muted-foreground text-center">
                    {(page - 1) * pageSize + i + 1}
                  </td>
                  <td className="px-4 py-3 font-medium" title={t.title}>
                    {t.title.length > 30 ? t.title.slice(0, 30) + "..." : t.title}
                    {t.parent_title && (
                      <div className="text-[10px] text-muted-foreground">
                        Subtask of: {t.parent_title}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{projectName(t.project_id)}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-medium text-foreground">{userName(t.assignee_id)}</div>
                    {t.tagged_user_ids && t.tagged_user_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {t.tagged_user_ids.map((uid) => (
                          <Badge key={uid} variant="secondary" className="text-[10px] py-0 px-1 font-normal">
                            @{userName(uid).split(" ")[0]}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={priorityBadgeClass(t.priority)}>{t.priority}</Badge>
                  </td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className={statusBadgeClass(t.status)}>{TASK_STATUS_LABELS[t.status]}</Badge>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-center">{t.estimated_hours ?? "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-center">{t.deadline_changes || 0}</td>
                  <td className="px-3 py-3 text-xs font-mono text-muted-foreground">{t.due_date || "—"}</td>
                </tr>
              ))}
              {tasks.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">
                    No tasks match the current filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="tasks-pagination"
        />
      </Card>
    </div>
  );
}
